import React from "react";
import Link from "next/link";
import { Card, CardContent, CardHeader } from "@/components/cards/card/card";
import { ContentCard } from "@/types/cms";
import { renderPriceFeatures } from "@/lib/richText";
import { Button } from "@/components/button/button/button";
import { Badge } from "@/components/badge/badge";
import styles from "./cardPrice.module.css";

interface CardPriceProps {
  card: ContentCard;
  freeVariant?: boolean;
}

const CardPrice = ({ card, freeVariant = false }: CardPriceProps) => {
  const blocksFromCard = Array.isArray(card.content)
    ? card.content
    : Array.isArray(card.body)
      ? card.body
      : undefined;
  const blocks = blocksFromCard;

  // Pricing fields now come from the CMS `price` relation on the card.
  // Keep rendering of the features list from `card.content` unchanged.
  const rawPrice = (card as any)?.price;
  const priceAttrs =
    rawPrice?.data?.attributes ?? rawPrice?.attributes ?? rawPrice ?? null;

  const planTitle = (priceAttrs?.planTitle ?? "").toString();
  const planSubtitle = (priceAttrs?.planSubtitle ?? "").toString();
  const price = (priceAttrs?.price ?? "").toString();
  const strikeoutPrice = (priceAttrs?.strikeoutPrice ?? "").toString();
  const hasStrikeoutPrice = !!strikeoutPrice.trim();
  const planSavings = (priceAttrs?.savings ?? "").toString();
  const badgeText = (priceAttrs?.badge ?? "").toString().trim();

  // Filter out all headings to get features/bullets (price fields no longer come from headings)
  const otherBlocks = blocks?.filter((b: any) => b.type !== "heading");

  // Free variant: pull the first paragraph out so it can render above the CTA.
  let leadBlock: any = null;
  let featureBlocks = otherBlocks;
  if (freeVariant && Array.isArray(otherBlocks)) {
    const firstParaIdx = otherBlocks.findIndex(
      (b: any) => b?.type === "paragraph"
    );
    if (firstParaIdx >= 0) {
      leadBlock = otherBlocks[firstParaIdx];
      featureBlocks = [
        ...otherBlocks.slice(0, firstParaIdx),
        ...otherBlocks.slice(firstParaIdx + 1),
      ];
    }
  }
  
  const normalizeRelationArray = (rel: any): any[] => {
    if (!rel) return [];
    if (Array.isArray(rel)) return rel;
    if (Array.isArray(rel?.data)) return rel.data;
    return [];
  };

  // Get buttons from card (supports legacy nested `data` wrapper or plain arrays)
  const buttons = normalizeRelationArray((card as any).buttons).map((item: any) => {
    const attrs = item?.attributes ?? item ?? {};
    return {
      id: item?.id,
      copy: attrs.copy,
      title: attrs.title,
      url: attrs.url,
      type: attrs.type,
      target: attrs.target,
      button_customID: attrs.button_customID,
      size: attrs.size,
    };
  });

  const mapVariant = (raw: unknown) => {
    const val = String(raw || "").toLowerCase().trim();
    if (
      val === "primary" ||
      val === "default" ||
      val === "secondary" ||
      val === "ghost" ||
      val === "link" ||
      val === "destructive"
    ) {
      return val as any;
    }
    return "default" as const;
  };

  // CMS uses `xs/sm/md/lg/xl`; Button supports `default/sm/lg/icon`.
  const mapSize = (raw: unknown): "default" | "sm" | "lg" => {
    const val = String(raw || "").toLowerCase().trim();
    if (val === "xs" || val === "sm" || val === "small") return "sm";
    if (val === "lg" || val === "xl" || val === "large") return "lg";
    return "default";
  };

  // Use the first button with a URL (or first button) as the primary CTA for the whole card
  const primaryButton =
    buttons.find((b: any) => b?.url) || (buttons.length > 0 ? buttons[0] : null);

  const rawPrimaryHref = (primaryButton?.url || "").trim();
  const primaryCustomId = primaryButton?.button_customID
    ? String(primaryButton.button_customID).toLowerCase().trim()
    : "";
  const primaryIsEmailLink = primaryCustomId === "email";

  const primaryHref = primaryIsEmailLink
    ? rawPrimaryHref
      ? rawPrimaryHref.toLowerCase().startsWith("mailto:")
        ? rawPrimaryHref
        : `mailto:${rawPrimaryHref}`
      : "#"
    : rawPrimaryHref || "#";

  const primaryTarget = String(primaryButton?.target || "self").toLowerCase();
  const primaryIsBlank = !primaryIsEmailLink && primaryTarget === "blank";

  const cardInner = (
    <Card className={styles.root} data-variant={freeVariant ? "free" : undefined}>
      <CardHeader className={styles.header}>
        {/* Plan name, device count, and optional badge on same top row */}
        {(planTitle || planSubtitle || badgeText) && (
          <div className={styles.titleRow}>
            <div className={styles.titleGroup}>
              {planTitle && (
                <h3 className={styles.planTitle}>
                  {planTitle}
                </h3>
              )}
              {planSubtitle && (
                <p className={styles.deviceCount}>
                  {planSubtitle}
                </p>
              )}
            </div>
            {badgeText && (
              <Badge className={styles.planBadge}>
                {badgeText}
              </Badge>
            )}
          </div>
        )}
        
        {/* Price, savings badge, then optional monthly price label on the same line */}
        {(price || strikeoutPrice) && (
          <div className={styles.priceRow}>
            <p className={styles.price}>
              {price}
            </p>
            {planSavings && (
              <Badge className={styles.planSavingsBadge}>{planSavings}</Badge>
            )}
            {strikeoutPrice && (
              <p className={styles.strikeoutPrice}>
                {strikeoutPrice}
              </p>
            )}
          </div>
        )}
      </CardHeader>
      
      <CardContent className={styles.content}>
        {/* Free variant: first body paragraph appears above the CTA */}
        {freeVariant && leadBlock && (
          <div className={styles.features}>
            {renderPriceFeatures([leadBlock], {
              feature: styles.feature,
              featureTitle: styles.featureTitle,
              featureSubtitle: styles.featureSubtitle,
              featureGroup: styles.featureGroup,
            })}
          </div>
        )}

        {/* Buttons (visual CTAs inside the clickable card) */}
        {buttons.length > 0 && (
          <div className={styles.buttons}>
            {buttons.map((button: any, idx: number) => (
              <Button
                key={button.id ?? idx}
                variant={mapVariant(button.type)}
                size={mapSize(button.size)}
                asChild
                className={styles.ctaButton}
              >
                {/* Use a span so we don't nest anchors inside the outer Link.
                    Clicking this still triggers the outer card link. */}
                <span>{button.copy || button.title}</span>
              </Button>
            ))}
          </div>
        )}

        {/* Features list (remainder, after any extracted lead paragraph) */}
        {featureBlocks && featureBlocks.length > 0 && (
          <div className={styles.features}>
            {renderPriceFeatures(featureBlocks, {
              feature: styles.feature,
              featureTitle: styles.featureTitle,
              featureSubtitle: styles.featureSubtitle,
              featureGroup: styles.featureGroup,
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // If we have a primary CTA, make the entire card clickable with that link
  if (primaryButton && primaryHref && primaryHref !== "#") {
    return (
      <Link
        href={primaryHref}
        target={primaryIsBlank ? "_blank" : undefined}
        rel={primaryIsBlank ? "noopener noreferrer" : undefined}
        className={styles.clickable}
      >
        {cardInner}
      </Link>
    );
  }

  // Fallback: non-clickable card if no buttons/URLs are defined
  return cardInner;
};

export default CardPrice;


